import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, activeRole } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const solutionsRef = useRef<HTMLDivElement>(null);
  const companyRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (solutionsRef.current && !solutionsRef.current.contains(e.target as Node)) setSolutionsOpen(false);
      if (companyRef.current && !companyRef.current.contains(e.target as Node)) setCompanyOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/50 shadow-sm"
        : "bg-transparent"
        }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-display font-bold text-2xl tracking-tight text-slate-900 group-hover:text-accent transition-colors">
              <span className="text-[0.9em] opacity-80 mr-1.5">//</span>Intellr
              <span className="text-accent">.</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Solutions Dropdown */}
            <div ref={solutionsRef} className="relative">
              <button
                onClick={() => {
                  setSolutionsOpen(!solutionsOpen);
                  setCompanyOpen(false);
                }}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/for-employers") || isActive("/how-it-works")
                  ? "text-accent bg-accent/5"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/50"
                  }`}
              >
                Solutions
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${solutionsOpen ? "rotate-180" : ""}`} />
              </button>
              {solutionsOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 rounded-xl bg-white border border-slate-200/60 shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                  <Link
                    to="/for-employers"
                    className="block px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-accent hover:bg-slate-50 transition-colors"
                    onClick={() => setSolutionsOpen(false)}
                  >
                    For Employers
                    <span className="block text-[10px] text-slate-400 mt-0.5">AI-powered hiring pipeline</span>
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="block px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-accent hover:bg-slate-50 transition-colors"
                    onClick={() => setSolutionsOpen(false)}
                  >
                    How It Works
                    <span className="block text-[10px] text-slate-400 mt-0.5">Step-by-step walkthrough</span>
                  </Link>
                  <Link
                    to="/jobs"
                    className="block px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-accent hover:bg-slate-50 transition-colors"
                    onClick={() => setSolutionsOpen(false)}
                  >
                    For Candidates
                    <span className="block text-[10px] text-slate-400 mt-0.5">Browse open positions</span>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/pricing"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/pricing")
                ? "text-accent bg-accent/5"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/50"
                }`}
            >
              Pricing
            </Link>

            <Link
              to="/case-studies"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/case-studies")
                ? "text-accent bg-accent/5"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/50"
                }`}
            >
              Case Studies
            </Link>

            {/* Company Dropdown */}
            <div ref={companyRef} className="relative">
              <button
                onClick={() => {
                  setCompanyOpen(!companyOpen);
                  setSolutionsOpen(false);
                }}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/about") || isActive("/contact")
                  ? "text-accent bg-accent/5"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/50"
                  }`}
              >
                Company
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${companyOpen ? "rotate-180" : ""}`} />
              </button>
              {companyOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 rounded-xl bg-white border border-slate-200/60 shadow-xl py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                  <Link
                    to="/about"
                    className="block px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-accent hover:bg-slate-50 transition-colors"
                    onClick={() => setCompanyOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link
                    to="/contact"
                    className="block px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-accent hover:bg-slate-50 transition-colors"
                    onClick={() => setCompanyOpen(false)}
                  >
                    Contact
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <Button variant="hero" size="sm" asChild className="px-6 h-9">
                <Link to="/dashboard">
                  Go to Dashboard
                </Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild className="font-medium text-slate-600 hover:text-slate-900">
                  <Link to="/auth?mode=login">Sign In</Link>
                </Button>
                <Button variant="hero" size="sm" asChild className="px-6 h-9">
                  <Link to="/auth?mode=signup">Get Started</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-slate-900" /> : <Menu className="w-5 h-5 text-slate-900" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 animate-in fade-in slide-in-from-top-4 duration-300 bg-white/95 backdrop-blur-2xl border-t border-slate-100">
            <div className="flex flex-col gap-1">
              {[
                { to: "/for-employers", label: "For Employers" },
                { to: "/how-it-works", label: "How It Works" },
                { to: "/jobs", label: "Browse Jobs" },
                { to: "/pricing", label: "Pricing" },
                { to: "/case-studies", label: "Case Studies" },
                { to: "/about", label: "About" },
                { to: "/contact", label: "Contact" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-6 py-3.5 rounded-xl text-base font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-6 flex flex-col gap-3 pt-6 mt-3 border-t border-slate-100">
                {user ? (
                  <Button variant="hero" asChild className="w-full h-12">
                    <Link to={activeRole === "employer" ? "/employer/dashboard" : "/dashboard"} onClick={() => setMobileMenuOpen(false)}>
                      Go to Dashboard
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button variant="hero-outline" asChild className="w-full h-12">
                      <Link to="/auth?mode=login" onClick={() => setMobileMenuOpen(false)}>
                        Sign In
                      </Link>
                    </Button>
                    <Button variant="hero" asChild className="w-full h-12">
                      <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)}>
                        Get Started
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
